import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
  type ToolErrorCode,
  type ToolOperation,
  type ToolPresetMask,
} from '@saas-pages/shared/public/runtime'
import { getTool, type ToolKey } from '@/shared/tools/tools'

interface OperationOptions {
  presetMask?: ToolPresetMask
  errorCode?: ToolErrorCode | ((error: unknown) => ToolErrorCode)
}

interface EditingSession {
  run: <Result>(hasContent: boolean, work: () => Result) => Result
}

export const TXTIFY_ANALYTICS_TOOLS = {
  textCounter: 'textCounter',
  textCase: 'textCase',
  passwordGenerator: 'passwordGenerator',
  seoLimits: 'seoLimits',
} as const satisfies Record<string, ToolKey>

function context(
  toolKey: ToolKey,
  presetMask?: ToolPresetMask,
): ToolAnalyticsContext {
  const tool = getTool(toolKey)
  return {
    app: 'txtify',
    domain: 'txtify.ru',
    route: tool.path,
    utility: tool.analyticsKey,
    processingMode: tool.processing,
    ...(presetMask === undefined ? {} : { presetMask }),
  }
}

export function startTxtifyToolOperation(
  toolKey: ToolKey,
  presetMask?: ToolPresetMask,
): ToolOperation {
  return startToolOperation(context(toolKey, presetMask))
}

export function runTxtifyToolOperation<Result>(
  toolKey: ToolKey,
  work: () => Result,
  options: OperationOptions = {},
): Result {
  const startedAt = performance.now()
  const operation = startTxtifyToolOperation(toolKey, options.presetMask)

  try {
    const result = work()
    operation.success(performance.now() - startedAt)
    return result
  } catch (error) {
    const errorCode = typeof options.errorCode === 'function'
      ? options.errorCode(error)
      : options.errorCode ?? 'processing_failed'
    operation.error(errorCode)
    throw error
  }
}

export function rejectTxtifyToolOperation(
  toolKey: ToolKey,
  errorCode: ToolErrorCode,
  presetMask?: ToolPresetMask,
): void {
  trackToolRejection(context(toolKey, presetMask), errorCode)
}

export function createTxtifyEditingSession(
  toolKey: ToolKey,
): EditingSession {
  let active = false

  return {
    run<Result>(hasContent: boolean, work: () => Result): Result {
      if (!hasContent) {
        active = false
        return work()
      }

      if (active) {
        return work()
      }

      const result = runTxtifyToolOperation(toolKey, work)
      active = true
      return result
    },
  }
}
